import { useEffect, useState } from 'react';

import GalleryIcon from '@/assets/all-icons/gallery-icon';
import config from '@/config';
import useGetSearchParams from '@/hooks/useGetSearchParams';
import useQuery from '@/hooks/useQuery';
import useTranslations from '@/i18n/useTranslations';
import useSearchRandomizedFeed from '@/pages/search/hooks/useSearchRandomizedFeed';
import fetchWrapper from '@/services/fetchWrapper.service';
import HomeHeader from '@/shared-components/home/components/HomeHeader';
import Loader from '@/shared-components/loader/Loader';
import StaticSeoHead from '@/shared-components/seo/StaticSeoHead';
import Text from '@/shared-components/text/components/Text';
import AppNavigationPage from '@/shared-components/wrappers/components/AppNavigationPage';
import { getSsrSearchLandingBootstrap } from '@/utils/ssrSearchLandingBootstrap';

import RandomizedHomeLinksList from '../components/RandomizedHomeLinksList';
import useRandomizedHomeLinks from '../hooks/useRandomizedHomeLinks';

import VirtualList from "./components/VirtualList";

import './index.css';

const RANDOM_LIMIT = 28;
const RANDOM_ENDPOINT = `${config.apis.api.url}/home/links/randomized/galleries`;

function readInitialSearchLandingSeoPayload() {
  const payload = getSsrSearchLandingBootstrap('galleries');
  return payload?.seoData && typeof payload.seoData === 'object' ? payload.seoData : null;
}

function HomeGalleriesRandomized() {
  const tSearchCategories = useTranslations('common.shared.search.categories');
  const [searchSeo, setSearchSeo] = useState(() => readInitialSearchLandingSeoPayload());
  const randomizedState = useRandomizedHomeLinks({
    endpoint: RANDOM_ENDPOINT,
    limitParam: 'gallery_limit',
    limit: RANDOM_LIMIT,
    errorMessage: 'Unable to refresh galleries at the moment.',
  });

  useEffect(() => {
    let ignore = false;

    async function loadSearchSeo() {
      try {
        const { response, data } = await fetchWrapper.get(`${config.apis.api.url}/seo/search/galleries`);
        if (ignore || !response?.ok || !data || typeof data !== 'object') {
          return;
        }

        setSearchSeo(data);
      } catch (error) {
        if (!ignore) {
          console.error('Failed to load galleries SEO payload', error);
        }
      }
    }

    loadSearchSeo();

    return () => {
      ignore = true;
    };
  }, []);

  return (
    <>
      <StaticSeoHead
        title={searchSeo?.title || "Fine Art Galleries | Suarte"}
        description={searchSeo?.metaDescription || "Discover fine art galleries on Suarte. Explore represented artists, curated artworks, and original paintings available to collect from galleries around the world."}
        canonicalUrl={searchSeo?.canonicalUrl}
        canonicalPath={searchSeo?.canonicalPath || "/galleries"}
        robots={searchSeo?.robots}
        ogType={searchSeo?.ogType}
        image={searchSeo?.ogImage}
        structuredData={searchSeo?.structuredData}
        pageType="CollectionPage"
        breadcrumbItems={[
          { name: 'Home', url: 'https://suarte.art/' },
          { name: 'Galleries', url: 'https://suarte.art/galleries' },
        ]}
      />
      <AppNavigationPage>
        <RandomizedHomeLinksList
          className="home-galleries__page home-galleries__randomized"
          randomizedState={randomizedState}
          emptyText="No galleries available at the moment."
          emptyTextClassName="home-galleries__empty-text font-size-medium"
          VirtualListComponent={VirtualList}
          virtualListProps={{ showSearchCTA: true }}
        >
          <HomeHeader
            onInput={() => {}}
            value=""
            icon={<GalleryIcon className="home-galleries__header-icon" />}
            text={tSearchCategories('galleries')}
            placeholder="Search for galleries..."
            hideSearch
          />
        </RandomizedHomeLinksList>
      </AppNavigationPage>
    </>
  );
}

function HomeGalleriesSearch({ valueFromSearch = "", fromSearch = false }) {
  const [params, setParams] = useGetSearchParams();
  const [query, setQuery] = useState(params ? params : { q: "" });
  const searchValue = typeof valueFromSearch?.q === "string" ? valueFromSearch.q.trim() : "";
  const shouldUseRandomizedResults = searchValue.length === 0;
  const randomizedState = useSearchRandomizedFeed({
    feedKey: 'galleries',
    endpoint: RANDOM_ENDPOINT,
    limitParam: 'gallery_limit',
    limit: RANDOM_LIMIT,
    enabled: shouldUseRandomizedResults,
    errorMessage: 'Unable to refresh galleries at the moment.',
  });
  const { loading, queryData, loadMoreData } = useQuery(
    "home_galleries",
    `${config.apis.api.url}/search/gallery`,
    query,
    {
      optionalToken: true,
      customQueryValidation: (nextQuery) => Boolean(nextQuery?.q && nextQuery.q.trim()),
    }
  );

  function onInput(value) {
    const newQuery = { q: typeof value === "string" ? value : "" };
    setQuery(newQuery);
    setParams(newQuery, { replace: true });
  }

  useEffect(() => {
    onInput(searchValue);
  }, [searchValue]);

  if (shouldUseRandomizedResults) {
    return (
      <RandomizedHomeLinksList
        className="home-galleries__page"
        randomizedState={randomizedState}
        emptyText="No galleries available at the moment."
        emptyTextClassName="home-galleries__empty-text font-size-medium"
        VirtualListComponent={VirtualList}
        virtualListProps={{ fromSearch }}
      />
    );
  }

  return (
    <div className="home-galleries__page">
      {loading ? (
        <Loader />
      ) : queryData.data.data.length === 0 ? (
        <Text className="home-galleries__empty-text font-size-medium" paragraph>
          No galleries available for your search query.
        </Text>
      ) : (
        <VirtualList
          key={queryData.queryString}
          items={queryData.data}
          onLoadMore={loadMoreData}
          fromSearch={fromSearch}
        />
      )}
    </div>
  );
}

export default function HomeGalleries(props) {
  if (props.fromSearch) {
    return <HomeGalleriesSearch {...props} />;
  }
  return <HomeGalleriesRandomized />;
}
